/*
 * product-layout.js: the price/rating presentation and colour + price line in the docking add-to-cart row.
 * The Traffic Handle Leash and handsfree adapter price blocks group the existing adjacent Loox rating above Shop Pay terms; the payment
 * terms form and its input stay in place. The docking line reads the preview's combo line when a
 * preview exists and shows a real selection (not its example colourway), otherwise the Globo colour fields in DOM
 * order (Colour, Handle Colour / Accent Colour), otherwise the siblings block's hidden Color property (classic
 * leashes). THL presentation moves whole existing colour options and the native Hardware wrapper without
 * changing any input or selection. The scoped theme sellout getter sorts by option position, not DOM order.
 * HFA keeps the native Width / Hardware / Size DOM order required by Camouflage; CSS alone places
 * Hardware first visually. Its native keyboard order and all app-owned selectors remain unchanged.
 * One instance per product section; every listener and timer is removed on shopify:section:unload and the
 * instance is re-created on shopify:section:load (theme editor re-render). Authoritative copy:
 * assets/product-layout.js in the staged theme (the prototype loads the matching working copy).
 */
(function () {
  'use strict';
  if (window.__stLayoutLoaded) return;
  window.__stLayoutLoaded = true;

  function Layout(section) {
    this.section = section;
    this.offs = [];
    this.raf = null;
    this.timers = [];
    this.destroyed = false;
    this.swatchRecords = [];
    this.adapterGroups = [];
    this.collarGroups = [];
    this.swatchFrame = null;
    this.initSwatches();
    this.groupPriceRating();
    var row = section.querySelector('.product__block--buttons');
    if (!row) return;
    this.combo = row.querySelector('.product__combo'); // adopt an existing line (re-boot on the same markup) or add one
    if (!this.combo) {
      this.combo = document.createElement('div');
      this.combo.className = 'product__combo';
      row.insertBefore(this.combo, row.firstChild);
    }
    this.priceEl = section.querySelector('.product__price [data-product-price], [data-product-price]');
    this.bind();
    this.refresh();
    this.later(1500); // Globo renders late; the events above normally cover it
  }

  Layout.prototype.groupPriceRating = function () {
    if (!/^(biothane-th-leash|handsfree-adapter|qr-collar|classic-leash-unified|classic-collar-unified|safety-strap-preview|multi-dog-preview)$/.test(this.section.getAttribute('data-pv-template'))) return;
    var wrap = this.section.querySelector('.product__price__wrap');
    var block = wrap && wrap.closest('.product__block');
    var rating = block && block.nextElementSibling;
    // The editor places rating immediately after Price. Only move that presentation block; leave Shop Pay's
    // form, custom element and variant input in their existing parent so their live state is preserved.
    if (!rating || !rating.matches('.shopify-app-block') || rating.id.indexOf('loox_reviews_loox_rating_') < 0) return;
    this.priceSummary = { block: block, rating: rating, parent: block.parentNode };
    block.setAttribute('data-price-summary', '');
    block.insertBefore(rating, block.querySelector('.shop-pay-terms'));
  };
  Layout.prototype.restorePriceRating = function () {
    var summary = this.priceSummary;
    if (!summary) return;
    if (summary.rating.parentNode === summary.block && summary.block.parentNode === summary.parent) {
      summary.parent.insertBefore(summary.rating, summary.block.nextElementSibling);
    }
    summary.block.removeAttribute('data-price-summary');
    this.priceSummary = null;
  };

  Layout.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.offs.push(function () { target.removeEventListener(type, fn, opts); });
  };
  // Keep the real controls and their explicit form ownership. Globo owns all of its existing boxed groups;
  // only the native Hardware wrapper is inserted between two intact groups. Re-rendering the app may remove
  // this wrapper, so retain its original parent and reattach that same live node when both anchors return.
  Layout.prototype.placeHardware = function () {
    var record = this.hardwarePlacement;
    if (record && !this.section.contains(record.parent)) { this.hardwarePlacement = null; record = null; }
    var handle = this.section.querySelector('.gpo-app .gpo-form__group[data-element-id="color-swatches-2"]');
    var position = this.section.querySelector('.gpo-app .gpo-form__group[data-element-id="buttons-1"]');
    var target = handle && handle.parentNode;
    if (!target || !target.matches('.gpo-group__childs') || !position || position.parentNode !== target) {
      this.restoreHardware();
      return;
    }
    if (!record) {
      var node = this.section.querySelector('.product__selectors .selector-wrapper[data-option-position="2"]');
      if (!node || !node.parentNode.matches('.product__selectors')) return;
      var inputs = node.querySelectorAll('input');
      if (inputs.length !== 2 || Array.prototype.some.call(inputs, function (input) {
        return input.type !== 'radio' || !/^(Brass|Chrome)$/.test(input.value) ||
          input.getAttribute('data-index') !== 'option2' || !input.hasAttribute('data-single-option-selector') ||
          !input.getAttribute('form') || input.getAttribute('form') !== inputs[0].getAttribute('form');
      })) return;
      record = { node: node, parent: node.parentNode, next: node.nextElementSibling };
      this.hardwarePlacement = record;
    }
    if (record.node.parentNode === target && record.node.nextElementSibling === position) return;
    var focused = document.activeElement;
    target.insertBefore(record.node, position);
    if (focused && record.node.contains(focused) && document.activeElement !== focused && focused.focus) focused.focus({ preventScroll: true });
  };
  // This is a presentation marker on a verified group, never on a submitted control. It supports today's
  // dropdowns and the observed colour/image swatch ID families without relying on an ID to identify the field name.
  Layout.prototype.adapterColourGroups = function (fieldRoles) {
    var self = this, accepted = [];
    this.adapterGroups = this.adapterGroups.filter(function (record) {
      if (self.section.contains(record.group)) return true;
      if (record.marker === null) record.group.removeAttribute('data-pv-colour-field');
      else record.group.setAttribute('data-pv-colour-field', record.marker);
      return false;
    });
    var groups = this.section.querySelectorAll('.gpo-app .gpo-form__group');
    Array.prototype.forEach.call(groups, function (group) {
      if (!fieldRoles && !/^(select|color-swatches|image-swatches|swatch)-[12]$/.test(group.getAttribute('data-element-id') || '')) return;
      var controls = group.querySelectorAll('select[data-field-name], input[data-field-name]');
      if (!controls.length) return;
      var field = controls[0].getAttribute('data-field-name');
      if (fieldRoles ? !Object.prototype.hasOwnProperty.call(fieldRoles, field) : (field !== 'Colour' && field !== 'Accent Colour')) return;
      if (Array.prototype.some.call(controls, function (control) {
        return control.getAttribute('data-field-name') !== field || (control.tagName !== 'SELECT' && control.type !== 'radio') ||
          (fieldRoles && control.closest('.gpo-form__group') !== group);
      })) return;
      if (!self.adapterGroups.some(function (record) { return record.group === group; })) {
        self.adapterGroups.push({ group: group, marker: group.getAttribute('data-pv-colour-field') });
      }
      group.setAttribute('data-pv-colour-field', fieldRoles ? fieldRoles[field] : (field === 'Colour' ? 'main' : 'accent'));
      accepted.push(group);
    });
    this.adapterGroups = this.adapterGroups.filter(function (record) {
      if (accepted.indexOf(record.group) >= 0) return true;
      if (record.marker === null) record.group.removeAttribute('data-pv-colour-field');
      else record.group.setAttribute('data-pv-colour-field', record.marker);
      return false;
    });
    return accepted;
  };
  Layout.prototype.restoreHardware = function () {
    var record = this.hardwarePlacement;
    if (!record) return;
    if (this.section.contains(record.parent)) {
      var focused = document.activeElement;
      record.parent.insertBefore(record.node, record.next && record.next.parentNode === record.parent ? record.next : null);
      if (focused && record.node.contains(focused) && document.activeElement !== focused && focused.focus) focused.focus({ preventScroll: true });
    }
    this.hardwarePlacement = null;
  };
  Layout.prototype.collarFields = function () {
    var self = this, found = { Colour: [], Size: [] }, accepted = [];
    Array.prototype.forEach.call(this.section.querySelectorAll('.gpo-app .gpo-form__group'), function (group) {
      var controls = group.querySelectorAll('select[data-field-name], input[data-field-name]');
      if (!controls.length) return;
      var field = controls[0].getAttribute('data-field-name');
      if (!Object.prototype.hasOwnProperty.call(found, field) || Array.prototype.some.call(controls, function (control) {
        return control.getAttribute('data-field-name') !== field || (control.tagName !== 'SELECT' && control.type !== 'radio');
      })) return;
      found[field].push(group);
    });
    Object.keys(found).forEach(function (field) {
      if (found[field].length !== 1) return;
      var group = found[field][0], attr = field === 'Colour' ? 'data-pv-colour-field' : 'data-pv-size-field';
      if (!self.collarGroups.some(function (record) { return record.group === group && record.attr === attr; })) {
        self.collarGroups.push({ group: group, attr: attr, value: group.getAttribute(attr) });
      }
      group.setAttribute(attr, field === 'Colour' ? 'main' : '');
      accepted.push(group);
    });
    this.collarGroups = this.collarGroups.filter(function (record) {
      if (accepted.indexOf(record.group) >= 0 && record.attr === (record.group === found.Colour[0] ? 'data-pv-colour-field' : 'data-pv-size-field')) return true;
      if (record.value === null) record.group.removeAttribute(record.attr);
      else record.group.setAttribute(record.attr, record.value);
      return false;
    });
    return { colour: found.Colour.length === 1 ? found.Colour[0] : null, size: found.Size.length === 1 ? found.Size[0] : null };
  };
  function saveProperties(node, names) {
    var values = {};
    names.forEach(function (name) {
      values[name] = { value: node.style.getPropertyValue(name), priority: node.style.getPropertyPriority(name) };
    });
    return values;
  }
  function restoreProperties(node, values) {
    Object.keys(values).forEach(function (name) {
      var original = values[name];
      if (original.value) node.style.setProperty(name, original.value, original.priority);
      else node.style.removeProperty(name);
    });
  }
  function setPixels(node, name, value) {
    var pixels = Math.round(value * 100) / 100 + 'px';
    if (node.style.getPropertyValue(name) !== pixels) node.style.setProperty(name, pixels);
  }
  Layout.prototype.restoreCollarPlacement = function () {
    var record = this.collarPlacement;
    if (!record) return;
    if (this.collarResizeObserver) { this.collarResizeObserver.disconnect(); this.collarResizeObserver = null; }
    restoreProperties(record.outer, record.styles);
    if (record.outerMarker === null) record.outer.removeAttribute(record.outerAttr);
    else record.outer.setAttribute(record.outerAttr, record.outerMarker);
    if (record.nodeMarker === null) record.node.removeAttribute(record.nodeAttr);
    else record.node.setAttribute(record.nodeAttr, record.nodeMarker);
    if (record.buckle) {
      if (record.buckleMarker === null) record.buckle.removeAttribute('data-pv-qrc-buckle');
      else record.buckle.setAttribute('data-pv-qrc-buckle', record.buckleMarker);
    }
    this.collarPlacement = null;
  };
  Layout.prototype.restoreCollarGroupOrder = function () {
    var record = this.collarGroupOrder;
    if (!record) return;
    if (record.node.parentNode === record.parent) {
      var focused = document.activeElement;
      record.parent.insertBefore(record.node, record.next && record.next.parentNode === record.parent ? record.next : null);
      if (focused && record.node.contains(focused) && document.activeElement !== focused && focused.focus) focused.focus({ preventScroll: true });
    }
    this.collarGroupOrder = null;
  };
  Layout.prototype.placeCollarHardware = function (fields, optionPosition) {
    optionPosition = optionPosition || 1;
    var classic = optionPosition === 2;
    var prefix = classic ? '--pv-classic-' : '--pv-qrc-';
    var outerAttr = classic ? 'data-pv-classic-layout' : 'data-pv-qrc-layout';
    var nodeAttr = classic ? 'data-pv-classic-hardware' : 'data-pv-qrc-hardware';
    var colour = fields.colour, size = fields.size, parent = colour && colour.parentNode;
    var outer = colour && colour.closest('.product__form__outer');
    var node = this.section.querySelector('.product__selectors .selector-wrapper[data-option-position="' + optionPosition + '"]');
    if (!parent || !parent.matches('.gpo-group__childs') || !size || size.parentNode !== parent || !outer || !outer.contains(size) || !node || !outer.contains(node) ||
        !node.parentNode.matches('.product__selectors')) {
      this.restoreCollarPlacement(); this.restoreCollarGroupOrder(); return;
    }
    var inputs = node.querySelectorAll('input');
    if (inputs.length !== 2 || Array.prototype.some.call(inputs, function (input) {
      return input.type !== 'radio' || !/^(Brass|Chrome)$/.test(input.value) ||
        input.getAttribute('data-index') !== 'option' + optionPosition || !input.hasAttribute('data-single-option-selector') ||
        !input.getAttribute('form') || input.getAttribute('form') !== inputs[0].getAttribute('form');
    })) { this.restoreCollarPlacement(); this.restoreCollarGroupOrder(); return; }
    // Keep Shopify's native option order and form ownership; position the QR buckle visually.
    var buckle = !classic && this.section.getAttribute('data-pv-template') === 'qr-collar' ?
      this.section.querySelector('.product__selectors > .selector-wrapper[data-option-position="3"]') : null;
    if (buckle) {
      var buckleInputs = buckle.querySelectorAll('input');
      if (!outer.contains(buckle) || buckleInputs.length !== 2 || Array.prototype.some.call(buckleInputs, function (input) {
        return input.type !== 'radio' || !/^(Black|Coyote Brown \(1 inch ONLY\))$/.test(input.value) ||
          input.getAttribute('data-index') !== 'option3' || !input.hasAttribute('data-single-option-selector') ||
          input.getAttribute('form') !== inputs[0].getAttribute('form');
      })) buckle = null;
    }
    if (this.collarGroupOrder && (this.collarGroupOrder.node !== colour || this.collarGroupOrder.parent !== parent)) this.restoreCollarGroupOrder();
    var siblings = Array.prototype.slice.call(parent.children);
    if (classic && siblings.indexOf(colour) > siblings.indexOf(size)) { this.restoreCollarPlacement(); return; }
    if (!classic && siblings.indexOf(colour) > siblings.indexOf(size)) {
      if (!this.collarGroupOrder) this.collarGroupOrder = { node: colour, parent: parent, next: colour.nextElementSibling };
      var focused = document.activeElement;
      parent.insertBefore(colour, size);
      if (focused && colour.contains(focused) && document.activeElement !== focused && focused.focus) focused.focus({ preventScroll: true });
    }
    var rect = colour.getBoundingClientRect(), dimensions = node.getBoundingClientRect();
    if (!rect.width || !rect.height || !dimensions.height) { this.restoreCollarPlacement(); return; }
    var record = this.collarPlacement;
    if (record && (record.node !== node || record.outer !== outer || record.colour !== colour || record.size !== size || record.buckle !== buckle)) {
      this.restoreCollarPlacement(); record = null;
    }
    if (!record) {
      record = { node: node, outer: outer, colour: colour, size: size, outerAttr: outerAttr, nodeAttr: nodeAttr,
        outerMarker: outer.getAttribute(outerAttr), nodeMarker: node.getAttribute(nodeAttr),
        buckle: buckle, buckleMarker: buckle && buckle.getAttribute('data-pv-qrc-buckle'),
        styles: saveProperties(outer, [prefix + 'hardware-top', prefix + 'hardware-left', prefix + 'hardware-width', prefix + 'size-gap', prefix + 'buckle-top']) };
      this.collarPlacement = record;
      var self = this;
      if (window.ResizeObserver) {
        this.collarResizeObserver = new ResizeObserver(function () { self.scheduleSwatches(); });
        [colour, size, node].forEach(function (target) { self.collarResizeObserver.observe(target); });
        if (buckle) this.collarResizeObserver.observe(buckle);
      }
    }
    var style = window.getComputedStyle(colour), leftPadding = parseFloat(style.paddingLeft) || 0, rightPadding = parseFloat(style.paddingRight) || 0;
    var width = rect.width - leftPadding - rightPadding;
    if (width <= 0) { this.restoreCollarPlacement(); return; }
    outer.setAttribute(outerAttr, ''); node.setAttribute(nodeAttr, '');
    if (buckle) buckle.setAttribute('data-pv-qrc-buckle', '');
    setPixels(outer, prefix + 'hardware-width', width);
    dimensions = node.getBoundingClientRect();
    var buckleHeight = buckle ? buckle.getBoundingClientRect().height : 0;
    setPixels(outer, prefix + 'size-gap', dimensions.height + (classic ? 36 : 54) + (buckle ? buckleHeight + 26 : 0));
    var offset = node.offsetParent || outer, origin = offset.getBoundingClientRect();
    var hardwareTop = rect.bottom - origin.top - (offset.clientTop || 0) + (offset.scrollTop || 0) + (classic ? 18 : 26);
    setPixels(outer, prefix + 'hardware-top', hardwareTop);
    if (buckle) setPixels(outer, prefix + 'buckle-top', hardwareTop + dimensions.height + 26);
    setPixels(outer, prefix + 'hardware-left', rect.left + leftPadding - origin.left - (offset.clientLeft || 0) + (offset.scrollLeft || 0));
  };
  var swatchOrder = ['Terracotta', 'Espresso', 'Bluebell', 'Midnight', 'Periwinkle', 'Clay', 'Moss', 'Cedar', 'Seafoam', 'Sage', 'Bubblegum', 'Lilac', 'Cherry', 'Pinot', 'Canary', 'Tangerine', 'Frost', 'Obsidian', 'Hibiscus'];
  function swatchItems(wrapper) {
    return Array.prototype.filter.call(wrapper.children, function (node) { return node.matches('.gpo-ov-item'); });
  }
  function placeSwatches(wrapper, ordered) {
    var focused = document.activeElement, moved = false;
    ordered.forEach(function (node, index) {
      var current = swatchItems(wrapper)[index];
      if (current !== node) { wrapper.insertBefore(node, current || null); moved = true; }
    });
    if (moved && focused && wrapper.contains(focused) && document.activeElement !== focused && focused.focus) {
      focused.focus({ preventScroll: true });
    }
  }
  Layout.prototype.initSwatches = function () {
    if (!/^(biothane-th-leash|handsfree-adapter|qr-collar|classic-leash-unified|classic-collar-unified|safety-strap-preview|multi-dog-preview)$/.test(this.section.getAttribute('data-pv-template'))) return;
    var self = this, refresh = function () { self.scheduleSwatches(); };
    ['gpoRenderCompleted', 'gpoElementChanged'].forEach(function (type) { self.on(document, type, refresh); });
    if (window.MutationObserver) this.swatchObserver = new MutationObserver(refresh);
    if (window.ResizeObserver) this.swatchResizeObserver = new ResizeObserver(refresh);
    else this.on(window, 'resize', refresh, { passive: true });
    if (this.swatchResizeObserver && /^(qr-collar|classic-leash-unified)$/.test(this.section.getAttribute('data-pv-template'))) {
      this.swatchResizeObserver.observe(this.section.querySelector('.product__form__outer') || this.section);
    }
    this.orderSwatches();
  };
  Layout.prototype.sizeSwatches = function (wrapper, count) {
    var width = wrapper.clientWidth;
    if (!width) return;
    var style = window.getComputedStyle ? window.getComputedStyle(wrapper) : null;
    var template = this.section.getAttribute('data-pv-template');
    var handle = (template === 'biothane-th-leash' && wrapper.closest('[data-element-id="color-swatches-2"]')) ||
      (template === 'classic-leash-unified' && wrapper.closest('[data-pv-colour-field="accent"]'));
    var phone = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    var fallbackSize = phone ? (handle ? 29 : 32) : (handle ? 32 : 36);
    var gap = style ? parseFloat(style.columnGap) : NaN;
    var size = style && style.getPropertyValue ? parseFloat(style.getPropertyValue('--pv-swatch-size')) : NaN;
    if (!isFinite(gap)) gap = phone ? 6 : (handle ? 9 : 10);
    if (!isFinite(size) || size <= 0) size = fallbackSize;
    // Read the prototype's field-specific size from CSS. Keep at least two column-first rows,
    // adding rows only when the actual product column cannot fit the complete set at that size.
    var capacity = Math.max(1, Math.min(Math.ceil(count / 2), Math.floor((width + gap) / (size + gap))));
    var rows = Math.ceil(count / capacity), columns = Math.ceil(count / rows);
    wrapper.style.setProperty('--pv-swatch-columns', String(columns));
    wrapper.style.setProperty('--pv-swatch-rows', String(rows));
    wrapper.setAttribute('data-pv-swatch-grid', '');
  };
  Layout.prototype.scheduleSwatches = function () {
    if (this.destroyed || this.swatchFrame) return;
    var self = this;
    this.swatchFrame = requestAnimationFrame(function () {
      self.swatchFrame = null;
      if (!self.destroyed) self.orderSwatches();
    });
  };
  Layout.prototype.orderSwatches = function () {
    if (this.destroyed) return;
    var self = this;
    if (this.swatchObserver) this.swatchObserver.disconnect();
    try {
      this.swatchRecords = this.swatchRecords.filter(function (record) {
        if (self.section.contains(record.wrapper)) return true;
        if (self.swatchResizeObserver) self.swatchResizeObserver.unobserve(record.wrapper);
        return false;
      });
      var trafficHandle = this.section.getAttribute('data-pv-template') === 'biothane-th-leash';
      var adapter = this.section.getAttribute('data-pv-template') === 'handsfree-adapter';
      var collar = this.section.getAttribute('data-pv-template') === 'qr-collar';
      var classicLeash = this.section.getAttribute('data-pv-template') === 'classic-leash-unified';
      var classicCollar = this.section.getAttribute('data-pv-template') === 'classic-collar-unified';
      var accessory = /^(safety-strap-preview|multi-dog-preview)$/.test(this.section.getAttribute('data-pv-template'));
      var classic = classicLeash || classicCollar || accessory;
      var collarFields = collar ? this.collarFields() : null;
      var groups = classic ? this.adapterColourGroups(classicLeash ? { Colour: 'main', 'Handle Colour': 'accent' } : { Colour: 'main' }) :
        (collar ? (collarFields.colour ? [collarFields.colour] : []) : (adapter ? this.adapterColourGroups() : this.section.querySelectorAll('.gpo-app .gpo-form__group[data-element-id="color-swatches-1"], .gpo-app .gpo-form__group[data-element-id="color-swatches-2"]')));
      groups = Array.prototype.slice.call(groups);
      var positionGroup = trafficHandle && this.section.querySelector('.gpo-app .gpo-form__group[data-element-id="buttons-1"]');
      if (positionGroup) groups.push(positionGroup);
      Array.prototype.forEach.call(groups, function (group) {
        var position = group === positionGroup;
        var field = position ? 'Handle Position' : classic ? (group.getAttribute('data-pv-colour-field') === 'main' ? 'Colour' : 'Handle Colour') :
          (collar ? 'Colour' : (adapter ? (group.getAttribute('data-pv-colour-field') === 'main' ? 'Colour' : 'Accent Colour') : (group.getAttribute('data-element-id') === 'color-swatches-1' ? 'Colour' : 'Handle Colour')));
        var wrapper = group.querySelector('.gpo-ov-wrapper');
        if (!wrapper) return;
        var items = swatchItems(wrapper);
        if (!items.length || items.some(function (item) {
          var input = item.querySelector('input');
          return !input || input.type !== 'radio' || input.getAttribute('data-field-name') !== field;
        })) return;
        if (position && (items.length !== 2 || items.map(function (item) { return item.querySelector('input').value; }).sort().join('|') !== 'High|Low')) return;
        var record = self.swatchRecords.filter(function (entry) { return entry.wrapper === wrapper; })[0];
        if (!record) {
          record = { wrapper: wrapper, original: [], styles: {}, grid: wrapper.getAttribute('data-pv-swatch-grid') };
          ['--pv-swatch-columns', '--pv-swatch-rows'].forEach(function (name) {
            record.styles[name] = { value: wrapper.style.getPropertyValue ? wrapper.style.getPropertyValue(name) : '', priority: wrapper.style.getPropertyPriority ? wrapper.style.getPropertyPriority(name) : '' };
          });
          self.swatchRecords.push(record);
          if (self.swatchResizeObserver) self.swatchResizeObserver.observe(wrapper);
        }
        record.original = record.original.filter(function (item) { return item.parentNode === wrapper; });
        items.forEach(function (item) { if (record.original.indexOf(item) < 0) record.original.push(item); });
        var order = position ? ['Low', 'High'] : swatchOrder;
        var ranked = items.map(function (item, index) {
          var rank = order.indexOf(item.querySelector('input').value);
          return { item: item, rank: rank < 0 ? order.length : rank, index: index };
        }).sort(function (a, b) { return a.rank - b.rank || a.index - b.index; });
        placeSwatches(wrapper, ranked.map(function (entry) { return entry.item; }));
        if (!position) self.sizeSwatches(wrapper, items.length);
      });
      if (collar) this.placeCollarHardware(collarFields);
      else if (classicLeash) {
        var accents = this.section.querySelectorAll('.gpo-app .gpo-form__group[data-pv-colour-field="accent"]');
        var addons = this.section.querySelectorAll('.gpo-app .gpo-form__group[data-element-id="checkbox-1"]');
        var checks = addons.length === 1 ? Array.prototype.filter.call(addons[0].querySelectorAll('input[data-field-name="Auto locking carabiner"]'), function (input) { return input.type === 'checkbox' && input.value === 'Yes'; }) : [];
        this.placeCollarHardware({ colour: accents.length === 1 ? accents[0] : null, size: checks.length === 1 ? addons[0] : null }, 2);
      }
      else if (!adapter && !classic) this.placeHardware();
    } finally {
      if (this.swatchObserver && !this.destroyed) this.swatchObserver.observe(this.section, { childList: true, subtree: true });
    }
  };
  Layout.prototype.restoreSwatches = function () {
    this.restoreCollarPlacement();
    this.restoreCollarGroupOrder();
    this.collarGroups.forEach(function (record) {
      if (record.value === null) record.group.removeAttribute(record.attr);
      else record.group.setAttribute(record.attr, record.value);
    });
    this.collarGroups = [];
    if (this.swatchObserver) { this.swatchObserver.disconnect(); this.swatchObserver = null; }
    if (this.swatchResizeObserver) { this.swatchResizeObserver.disconnect(); this.swatchResizeObserver = null; }
    if (this.swatchFrame) { cancelAnimationFrame(this.swatchFrame); this.swatchFrame = null; }
    this.swatchRecords.forEach(function (record) {
      placeSwatches(record.wrapper, record.original.filter(function (item) { return item.parentNode === record.wrapper; }));
      Object.keys(record.styles).forEach(function (name) {
        var original = record.styles[name];
        if (original.value) record.wrapper.style.setProperty(name, original.value, original.priority);
        else if (record.wrapper.style.removeProperty) record.wrapper.style.removeProperty(name);
      });
      if (record.grid === null) record.wrapper.removeAttribute('data-pv-swatch-grid');
      else record.wrapper.setAttribute('data-pv-swatch-grid', record.grid);
    });
    this.swatchRecords = [];
    this.adapterGroups.forEach(function (record) {
      if (record.marker === null) record.group.removeAttribute('data-pv-colour-field');
      else record.group.setAttribute('data-pv-colour-field', record.marker);
    });
    this.adapterGroups = [];
    this.restoreHardware();
  };
  Layout.prototype.later = function (ms) {
    var self = this;
    var t = setTimeout(function () { self.timers = self.timers.filter(function (x) { return x !== t; }); self.refresh(); }, ms);
    this.timers.push(t);
  };
  Layout.prototype.bind = function () {
    var self = this, refresh = function () { self.refresh(); };
    ['change', 'input', 'click'].forEach(function (t) { self.on(self.section, t, refresh, { passive: true }); });
    ['gpoRenderCompleted', 'gpoElementChanged'].forEach(function (t) { self.on(document, t, refresh); });
    this.on(this.section, 'theme:variant:change', function () { self.later(50); });
    this.on(this.section, 'st-preview:state', refresh);
    this.on(this.section, 'st-preview:describe', refresh); // the preview's line changed (selection or example state)
  };

  Layout.prototype.pairFromPage = function () {
    var section = this.section;
    // the preview's line, unless it is showing its example colourway for unselected fields
    var root = section.querySelector('st-preview');
    var preview = root && root.querySelector('.st-preview__combo');
    if (preview && !root.hasAttribute('data-example') && !preview.hidden && preview.textContent.trim()) return preview.textContent.trim();
    var values = [], seen = {};
    var fields = section.querySelectorAll('.gpo-app [data-field-name]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i], name = el.getAttribute('data-field-name') || '';
      if (!/colou?r/i.test(name) || seen[name]) continue;
      var value = '';
      if (el.tagName === 'SELECT') value = el.value;
      else if (el.type === 'radio' || el.type === 'checkbox') { var checked = section.querySelector('.gpo-app input[data-field-name="' + name.replace(/["\\]/g, '\\$&') + '"]:checked'); value = checked ? checked.value : ''; }
      else value = el.value;
      seen[name] = true;
      if (value) values.push(value);
    }
    if (values.length) return values.join(' x ');
    var sib = section.querySelector('.product__siblings input[name^="properties["]');
    return sib ? sib.value : '';
  };
  Layout.prototype.refresh = function () {
    var self = this;
    if (this.destroyed || this.raf || !this.combo) return;
    this.raf = requestAnimationFrame(function () {
      self.raf = null;
      if (self.destroyed) return;
      try {
        var pair = self.pairFromPage(), price = self.priceEl ? self.priceEl.textContent.trim() : '';
        self.combo.textContent = '';
        var a = document.createElement('span'); a.textContent = pair;
        var b = document.createElement('span'); b.className = 'product__combo-price'; b.textContent = price;
        self.combo.appendChild(a); self.combo.appendChild(b);
      } catch (e) {}
    });
  };
  Layout.prototype.destroy = function () {
    this.destroyed = true;
    this.offs.forEach(function (off) { off(); }); this.offs = [];
    this.timers.forEach(function (t) { clearTimeout(t); }); this.timers = [];
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
    this.restoreSwatches();
    this.restorePriceRating();
    this.section = null; this.combo = null; this.priceEl = null;
  };

  function boot(scope) {
    var root = scope || document, sel = '[data-section-type="product"][data-pv-template]';
    var list = Array.prototype.slice.call(root.querySelectorAll(sel));
    if (root.matches && root.matches(sel)) list.unshift(root); // shopify:section:load targets the section wrapper; be safe either way
    list.forEach(function (s) {
      if (s.stLayout) return;
      try { s.stLayout = new Layout(s); } catch (e) {}
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); }); else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var s = e.target;
    if (!s) return;
    var list = s.matches && s.matches('[data-section-type="product"]') ? [s] : (s.querySelectorAll ? s.querySelectorAll('[data-section-type="product"]') : []);
    Array.prototype.forEach.call(list, function (el) { if (el.stLayout) { el.stLayout.destroy(); el.stLayout = null; } });
  });
})();



